import { IconName, IconPrefix, IconProp } from '@fortawesome/fontawesome-svg-core';

/**
 * Icon helpers.
 *
 * Legacy widgets store icons as camelCase keys (e.g. `'creditCard'`,
 * `'solidCalendarAlt'`). These are resolved to uxp's new `'prefix:name'` format via
 * `window.IconMetadata` — the CDN `icon-metadata.js` the host loads (`isLegacyKey` /
 * `convertLegacyKey`) — NOT a bundled icon table. New edits already store the new
 * format. Both resolve here and render through uxp `<Icon>` (see FAIcon).
 *
 * The old ~98 KiB `IconData.ts` map and the bespoke `<IconEditor>` modal are gone;
 * icon PICKING now uses uxp `IconPicker` (see IconPickerField).
 */

/** Resolve any stored icon value to a renderable string. Legacy keys convert via
 *  the CDN metadata; already-new-format / unknown values pass through unchanged.
 *  Returns `''` for empty input, and passes the value through untouched if the CDN
 *  metadata has not loaded yet. */
export function resolveIcon(value?: string | null): string {
    if (!value) return '';
    const M = (typeof window !== 'undefined' ? (window as any).IconMetadata : undefined);
    if (M && M.isLegacyKey(value)) return M.convertLegacyKey(value) || value;
    return value;
}

/** Split a resolved icon string (`'fas:credit-card'` or `'fas credit-card'`) into
 *  prefix + name. Returns null for empty / unresolvable (e.g. a bare legacy key when
 *  the CDN metadata is not yet loaded). */
function iconParts(value?: string | null): { prefix: string; name: string } | null {
    const s = resolveIcon(value);
    if (!s) return null;
    const sep = s.indexOf(':') !== -1 ? ':' : (s.indexOf(' ') !== -1 ? ' ' : '');
    if (!sep) return null;
    const [prefix, name] = s.split(sep);
    if (!prefix || !name) return null;
    return { prefix, name };
}

/** Legacy/new icon value → FontAwesome `IconProp` tuple, or null. */
function generateIconProp(icon: string): IconProp | null {
    const p = iconParts(icon);
    if (!p) return null;
    return [p.prefix as IconPrefix, p.name as IconName];
}

/** Legacy/new icon value → space-separated `'prefix name'` string, or null. */
function generateIconPropAsSting(icon: string): string | null {
    const p = iconParts(icon);
    if (!p) return null;
    return `${p.prefix} ${p.name}`;
}

export { generateIconProp, generateIconPropAsSting };
